import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import { fromApiDate } from 'v3/utils/rntrcValidation';

import api from 'services/api';

import { useDispatch } from 'react-redux';
import Details from '../Details';
import Register from '../Register';

import { ShippingCompanyProvider } from './context';

function getEmptyShippingCompany() {
  return {
    personType: { name: 'Jurídica' },
    cgccpf: '',
    ie: '',
    social_name: '',
    fantasy_name: '',
    rg: '',
    is_driver: false,
    email: '',
    phone: '',
    rntrc_number: '',
    rntrc_issue_date: '',
    rntrc_exp_date: '',
    rntrc_type: null,
    pispasep: '',
    shipment_link: null,
    addresses: [],
    vehicles: [],
    shippingDrivers: [],
  };
}

// SQL Server pode devolver bit como número ou string
function toNullableBoolean(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return null;
}

// Simples Nacional e início da emissão de contra CT-e ficam em shippingCompanies na API e no topo do formulário.
function mapRegistrationFieldsToForm(shippingCompany) {
  return {
    simples_nacional: toNullableBoolean(shippingCompany?.shippingCompanies?.simples_nacional),
    emission_start_date: fromApiDate(shippingCompany?.shippingCompanies?.emission_start_date),
  };
}

function mapRntrcToForm(shippingCompany) {
  return {
    rntrc_issue_date: fromApiDate(shippingCompany?.rntrc_issue_date),
    rntrc_exp_date: fromApiDate(shippingCompany?.rntrc_exp_date),
    rntrc_type: shippingCompany?.rntrc_type ? { id: null, name: shippingCompany.rntrc_type } : null,
  };
}

export default function ShippingCompanyShared() {
  const [shippingCompany, setShippingCompany] = useState(getEmptyShippingCompany());
  const [isFetching, setFetching] = useState(false);
  const [errors, setErrors] = useState({});
  const [prevShippingCompany, setPrevShippingCompany] = useState({});
  const snackbar = useSnackbar();
  const dispatch = useDispatch();

  const params = useParams();

  function showSnackbar(text, options) {
    snackbar.show(<Text> {text} </Text>, options);
  }

  function setData(data) {
    setShippingCompany((prev) => ({ ...prev, ...data }));
  }

  useEffect(() => {
    async function fetchShippingCompany() {
      try {
        setFetching(true);
        const response = await api.get(`v3/shipping-company/${params.id}`);
        const [firstShippingCompany] = response.data;

        setData({
          ...firstShippingCompany,
          shipment_link: firstShippingCompany?.shippingCompanies?.linkShippingCompany,
          ...mapRntrcToForm(firstShippingCompany),
          ...mapRegistrationFieldsToForm(firstShippingCompany),
          vehicles: firstShippingCompany.shippingCompanyVehicles,
        });

        setPrevShippingCompany((prev) => ({
          ...firstShippingCompany,
          ...mapRntrcToForm(firstShippingCompany),
          ...mapRegistrationFieldsToForm(firstShippingCompany),
          vehicles: firstShippingCompany.shippingCompanyVehicles,
        }));
      } catch (ex) {
        snackbar.show(<Text>Não foi possível carregar essa transportadora</Text>, {
          type: 'error',
        });
      } finally {
        setFetching(false);
      }
    }

    if (params.id) {
      fetchShippingCompany();
      return;
    }

    setShippingCompany(getEmptyShippingCompany());
    setPrevShippingCompany({});
    setErrors({});
    setFetching(false);
  }, [params.id]);

  useEffect(() => {
    if (params.id) {
      dispatch({
        type: 'VIEW_INSTANCE',
        instance: shippingCompany?.social_name,
      });
    }
  }, [shippingCompany]);

  return (
    <ShippingCompanyProvider
      value={{
        data: shippingCompany,
        setData,
        isFetching,
        errors,
        setErrors,
        showSnackbar,
        prevShippingCompany,
        setPrevShippingCompany,
      }}
    >
      {params.id ? <Details /> : <Register />}
    </ShippingCompanyProvider>
  );
}
